import { f as fetchConfig } from './index-DoPtEJTE.js';

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.options = {};

      if (this?.dataset?.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.options = { ...this.options, ...dataOptions };
      }

      this.form = this.querySelector('form');
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

      this.variants = this.querySelector('[data-variant-selector]');
      this.addEventListener('variant-change', this.onVariantChange.bind(this));

      // Prevent race conditions on add-to-cart
      this._addController = null;

      // Fallback guard if not defined elsewhere
      if (!window.__cartRequestGuard) {
        window.__cartRequestGuard = (() => {
          let locked = false;
          return {
            lock() { locked = true; },
            unlock() { locked = false; },
            isLocked() { return locked; },
          };
        })();
      }
    }

    onVariantChange(evt) {
      this.currentVariant = evt.detail.variant;
      this.toggleAddButton();

      this.updateMedia();
      this.updateURL();
      this.renderProductInfo();
    }

    updateMedia() {
      if (!this.currentVariant || !this.currentVariant?.featured_media) return;
      const newMedia = document.querySelector(
        `[data-media-id="${this.options.sectionId}-${this.currentVariant.featured_media.id}"]`
      );
      if (!newMedia) return;
      const parent = newMedia.parentElement;
      parent.prepend(newMedia);
      window.setTimeout(() => parent.scroll(0, 0));
    }

    updateURL() {
      if (!this.currentVariant || this.options.scope !== 'main') return;
      const url = new URL(document.location);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({}, '', url);
    }

    renderProductInfo() {
      if (!this.options.sectionId || !this.currentVariant?.id) return;

      fetch(`/products/${this.options.handle}?variant=${this.currentVariant.id}&section=${this.options.sectionId}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          this.updateElement(html, `price-${this.options.sectionId}`);
          this.updateElement(html, `inventory-${this.options.sectionId}`);
        })
        .catch(() => {});
    }

    updateElement(html, selector) {
      const source = html.getElementById(selector);
      const destination = document.getElementById(selector);

      if (source && destination) {
        if (destination.tagName === 'INPUT') destination.value = source.value;
        else destination.innerHTML = source.innerHTML;
      }
    }

    toggleAddButton() {
      this.addButton = this.addButton || this.querySelector('[name="add"]');
      this.addButtonText =
        this.addButtonText || this.addButton?.querySelector('[data-add-to-cart-button-text]');

      if (!this.addButton || !this.currentVariant) return;

      this.addButton.disabled = !this.currentVariant.available;

      if (!this.addButtonText) return;
      this.addButtonText.textContent = this.currentVariant.available
        ? window.variantStrings.addToCart
        : window.variantStrings.soldOut;
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();

      // Hard guard against double-adds (even if multiple forms exist)
      if (window.__cartRequestGuard.isLocked()) return;
      window.__cartRequestGuard.lock();

      // Abort any in-flight add request from this component
      if (this._addController) this._addController.abort();
      this._addController = new AbortController();

      this.submitButton = this.submitButton || this.querySelector('[type="submit"]');
      this.submitButton.setAttribute('disabled', true);
      this.submitButton.classList.add('button--loading');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      this.cartNotification = this.cartNotification || document.querySelector('cart-notification');
      this.cartDrawer = this.cartDrawer || document.querySelector('cart-drawer');
      this.cartItems = document.querySelector('cart-items');

      // IMPORTANT: open drawer immediately to stop users clicking again
      // Only do this when the behaviour is drawer
      if (this.submitButton?.dataset?.addToCartBehavior === 'open_cart_drawer' && this.cartDrawer) {
        // If you implemented the updated cart-drawer, this will show loading state
        this.cartDrawer.open({ showLoading: true });
        // Also let other cart components show loaders if needed
        document.dispatchEvent(new CustomEvent('cart:request-start'));
      }

      // Build sections to fetch (fixes your current bug where notification sections are ignored)
      let sectionsToFetch = [];

      if (this.cartNotification?.getSectionsToRender) {
        sectionsToFetch.push(...this.cartNotification.getSectionsToRender().map((s) => s.section));
      }

      if (this.cartItems?.getSectionsToRender) {
        sectionsToFetch.push(...this.cartItems.getSectionsToRender().map((s) => s.section));
      }

      sectionsToFetch = [...new Set(sectionsToFetch)].filter(Boolean);

      const formData = new FormData(this.form);
      if (sectionsToFetch.length > 0) formData.append('sections', sectionsToFetch);
      formData.append('sections_url', window.location.pathname);
      config.body = formData;
      config.signal = this._addController.signal;

      // Get current variant data
      this.currentVariant = this.querySelector('[data-variant-selector]')?.getCurrentVariantData?.();

      try {
        const response = await fetch(`${routes.cart_add_url}`, config);
        const parsedState = await response.json();

        // Quantity / validation errors
        if (parsedState.status === 422) {
          this.onCartError(parsedState, sectionsToFetch);
          return;
        }

        // Success path
        this.onCartSuccess(parsedState);

        // Broadcast to the whole document (so cart-items + cart-drawer listeners work)
        document.dispatchEvent(
          new CustomEvent('cart-updated', {
            detail: { ...parsedState },
          })
        );
      } catch (e) {
        // Abort = user quickly interacted again; don’t show error
        if (e?.name !== 'AbortError') console.error(e);
      } finally {
        this.submitButton.classList.remove('button--loading');
        this.submitButton.removeAttribute('disabled');

        // End loaders
        document.dispatchEvent(new CustomEvent('cart:request-end'));
        if (this.cartDrawer?.setLoading) this.cartDrawer.setLoading(false);

        window.__cartRequestGuard.unlock();
      }
    }

    onCartSuccess(parsedState) {
      // Render immediately if drawer exists
      if (this.cartDrawer?.renderContents) {
        this.cartDrawer.renderContents(parsedState);
      }

      switch (this.submitButton.dataset.addToCartBehavior) {
        case 'open_cart_drawer':
          // Already opened immediately; just ensure it stays open
          if (this.cartDrawer) this.cartDrawer.open();
          break;

        case 'show_cart_notification':
          if (this.cartNotification) {
            this.cartNotification.setActiveElement(document.activeElement);
            this.cartNotification.renderContents(parsedState);
          }
          break;
      }
    }

    onCartError(parsedState, sectionsToFetch) {
      if (this.cartNotification) {
        this.cartNotification.setActiveElement(document.activeElement);
        this.cartNotification.renderCartError(parsedState.description);
      }

      // Keep drawer open and stop loading if we opened it
      if (this.cartDrawer?.setLoading) this.cartDrawer.setLoading(false);

      // Optional: refresh drawer sections so it reflects actual cart state after error
      if (this.cartDrawer && sectionsToFetch?.length) {
        fetch('/?sections=' + sectionsToFetch.join())
          .then((response) => response.json())
          .then((sectionsObj) => {
            this.cartDrawer.renderContents({ sections: sectionsObj });
          })
          .catch(() => {});
      }
    }
  }

  window.ProductForm = ProductForm;
  customElements.define('product-form', ProductForm);
}
